// src/pages/settings/tabs/ProfileTab.jsx

import { useEffect, useMemo, useState, useRef } from 'react';

import { toast } from 'react-toastify';
import { Download, Eye, Upload } from 'lucide-react';
import { PdfPreviewDialog } from '../../../components/PdfPreviewDialog/PdfPreviewDialog';

import { useCurrentUser, useUpdateProfile } from '../../../features/auth/api';

import {
  useGetMyCustomer,
  useUploadMyCustomerDoc,
  useDownloadSelfCustomerDoc,
  usePreviewSelfCustomerDoc,
  type CustomerDoc,
  type CustomerDocType,
  type DocStatus,
} from '../../../features/customers/api';

import './profile-tab.scss';
import './profile-document-preview.scss';

const userTypeMapping = {
  ORG_OWNER: 'Organization Owner',

  ORG_ADMIN: 'Organization Admin',

  ORG_USER: 'Organization Member',

  USER: 'Customer',

  CARRIER: 'Carrier',
};

const statusStyles = {
  APPROVED: { bg: '#dcfce7', fg: '#166534', br: '#86efac', label: 'Approved' },

  UNDER_REVIEW: { bg: '#fef9c3', fg: '#854d0e', br: '#fde68a', label: 'Under review' },

  INVITED: { bg: '#eff6ff', fg: '#1d4ed8', br: '#bfdbfe', label: 'Invited' },

  REJECTED: { bg: '#fee2e2', fg: '#991b1b', br: '#fecaca', label: 'Rejected' },

  ARCHIVED: { bg: '#f3f4f6', fg: '#374151', br: '#e5e7eb', label: 'Archived' },
};

type CustomerTierSummary = {
  id: string;

  name: string;

  discountAmountCents: number;

  badgeColor: string;
};

function normalizeTierColor(color?: string | null) {
  const normalized = String(color ?? '')
    .trim()

    .toUpperCase();

  return /^#[0-9A-F]{6}$/.test(normalized) ? normalized : '#64748B';
}

function tierUsesDarkText(color?: string | null) {
  const hex = normalizeTierColor(color).slice(1);

  const red = parseInt(hex.slice(0, 2), 16);

  const green = parseInt(hex.slice(2, 4), 16);

  const blue = parseInt(hex.slice(4, 6), 16);

  const luminance = (0.299 * red + 0.587 * green + 0.114 * blue) / 255;

  return luminance > 0.62;
}

function formatTierDiscount(cents: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',

    currency: 'USD',
  }).format(Number(cents || 0) / 100);
}

function CustomerTierBadge({ tier }: { tier: CustomerTierSummary }) {
  const color = normalizeTierColor(tier.badgeColor);

  const textClass = tierUsesDarkText(color)
    ? 'customer-tier-badge--dark-text'
    : 'customer-tier-badge--light-text';

  return (
    <span className={`customer-tier-badge ${textClass}`}>
      <svg
        className="customer-tier-badge__background"
        viewBox="0 0 100 32"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <rect x="0" y="0" width="100" height="32" rx="16" fill={color} />
      </svg>

      <span className="customer-tier-badge__label">{tier.name}</span>
    </span>
  );
}

export default function ProfileTab() {
  const { data: user, isLoading } = useCurrentUser();

  const updateProfile = useUpdateProfile();

  const [form, setForm] = useState({ name: '', phone: '' });

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name ?? '',

        phone: user.phone ?? '',
      });
    }
  }, [user]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSave = () => {
    updateProfile.mutate(
      {
        name: form.name?.trim() || '',

        phone: form.phone?.trim() || '',
      },

      {
        onSuccess: () => toast.success('Changes saved.'),

        onError: () => toast.error('Could not save changes.'),
      }
    );
  };

  if (isLoading) {
    return (
      <div className="card">
        <div className="skeleton" style={{ height: 24, width: '30%', marginBottom: 12 }} />

        <div className="skeleton" style={{ height: 180, width: '100%' }} />
      </div>
    );
  }

  const isCustomer = user?.type === 'USER';

  return (
    <>
      <div className="card">
        <h2 className="card__title">Personal Information</h2>

        <p className="card__subtitle">Update your personal details and preferences</p>

        <div className="grid grid--2">
          <div className="form-control">
            <label htmlFor="name">Name</label>

            <input id="name" name="name" value={form.name} onChange={onChange} />
          </div>

          <div className="form-control">
            <label htmlFor="userType">Type</label>

            <input
              id="userType"
              name="userType"
              value={(userTypeMapping as Record<string, string>)[user?.type] || user?.type || ''}
              disabled
            />
          </div>

          <div className="form-control">
            <label htmlFor="email">Email Address</label>

            <input id="email" name="email" type="email" value={user?.email || ''} disabled />
          </div>

          <div className="form-control">
            <label htmlFor="phone">Phone Number</label>

            <input
              id="phone"
              name="phone"
              value={form.phone}
              onChange={onChange}
              placeholder="+1 (555) 123-4567"
            />
          </div>
        </div>

        <div className="actions">
          <button className="btn btn--primary" onClick={onSave} disabled={updateProfile.isPending}>
            {updateProfile.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {isCustomer && <CustomerVerificationCard />}
    </>
  );
}

function CustomerVerificationCard() {
  const { data, isLoading } = useGetMyCustomer();

  const upload = useUploadMyCustomerDoc();

  const fileRefs = useRef<Partial<Record<CustomerDocType, HTMLInputElement | null>>>({});

  const customer = data?.customer || null;

  const customerTier = (customer?.tier ?? null) as CustomerTierSummary | null;

  const status = customer?.status || 'INVITED';

  const s = statusStyles[status] || statusStyles.INVITED;

  const downloadDoc = useDownloadSelfCustomerDoc();
  const previewDoc = usePreviewSelfCustomerDoc();
  const previewRequestRef = useRef(0);
  const [previewingId, setPreviewingId] = useState<string | null>(null);
  const [preview, setPreview] = useState<{
    url: string;
    title: string;
    docId: string;
    filename: string;
  } | null>(null);

  // Ignore a PDF response if this tab has already unmounted.
  // PdfPreviewDialog owns and revokes the object URL when closed.
  useEffect(
    () => () => {
      previewRequestRef.current += 1;
    },
    []
  );

  const docsByType = useMemo(() => {
    const map: Record<CustomerDocType, CustomerDoc | null> = {
      AGREEMENT: null,

      RESALE_CERTIFICATE: null,
    };

    (customer?.documents || []).forEach((d) => {
      map[d.type] = d;
    });

    return map;
  }, [customer]);

  const items: {
    key: CustomerDocType;

    title: string;

    required: boolean;

    doc: CustomerDoc | null;
  }[] = [
    {
      key: 'AGREEMENT',

      title: 'Agreement',

      required: !!customer?.isAgreementRequered,

      doc: docsByType.AGREEMENT,
    },

    {
      key: 'RESALE_CERTIFICATE',

      title: 'Resale Certificate',

      required: !!customer?.isResaleCertificateRequered,

      doc: docsByType.RESALE_CERTIFICATE,
    },
  ];

  const onPick = (type: CustomerDocType) => async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];

    if (!f) return;

    // Basic guard: PDF only (adjust if you want to accept images)

    if (f.type !== 'application/pdf' && !f.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please upload a PDF file.');

      e.target.value = ''; // reset input

      return;
    }

    upload.mutate(
      { type, file: f },

      {
        onSuccess: () => {
          // Close a preview of the replaced file and discard an in-flight old version.
          previewRequestRef.current += 1;
          setPreviewingId(null);
          setPreview(null);
          toast.success(`${type === 'AGREEMENT' ? 'Agreement' : 'Resale certificate'} uploaded.`);

          e.target.value = ''; // reset for the same file re-select
        },

        onError: () => {
          toast.error('Upload failed. Please try again.');

          e.target.value = '';
        },
      }
    );
  };

  const handlePreview = async (docId: string, title: string, filename: string) => {
    const request = ++previewRequestRef.current;
    setPreviewingId(docId);
    setPreview(null);

    try {
      const blob = await previewDoc.mutateAsync({ docId });
      if (request !== previewRequestRef.current) return;

      const url = URL.createObjectURL(blob);
      setPreview({ url, title, docId, filename });
    } catch (error: any) {
      if (request === previewRequestRef.current) {
        toast.error(error?.response?.data?.error || 'Failed to preview PDF.');
      }
    } finally {
      if (request === previewRequestRef.current) {
        setPreviewingId(null);
      }
    }
  };

  const statusPill = (status: DocStatus) => {
    const m = {
      REJECTED: { bg: '#fee2e2', color: '#991b1b', label: 'Rejected, please reupload' },

      SUBMITTED: { bg: '#fef9c3', color: '#854d0e', label: 'Submitted' },
    };

    /*

     * `m.PENDING` was the fallback here and no such key exists, so any status

     * outside this map produced `undefined` and threw on `s.bg` a line later.

     * Falls back to the submitted styling instead.

     */

    const s = m[status] ?? m.SUBMITTED;

    return (
      <span
        style={{
          background: s.bg,

          color: s.color,

          border: '1px solid #e5e7eb',

          padding: '2px 8px',

          borderRadius: 999,

          fontSize: 12,

          fontWeight: 600,
        }}
      >
        {s.label}
      </span>
    );
  };

  return (
    <div className="card">
      {customerTier && (
        <div className="customer-tier-summary">
          <div className="customer-tier-summary__copy">
            <h2 className="card__title">Customer Tier</h2>

            <p className="card__subtitle">
              Your organization has assigned you this customer pricing tier.
            </p>
          </div>

          <div className="customer-tier-summary__value">
            <CustomerTierBadge tier={customerTier} />

            <span className="customer-tier-summary__discount">
              {formatTierDiscount(customerTier.discountAmountCents)} discount
            </span>
          </div>
        </div>
      )}

      <div className="flex-between" style={{ alignItems: 'center' }}>
        <div>
          <h2 className="card__title">Customer Verification</h2>

          <p className="card__subtitle">
            View your verification status and upload documents if needed.
          </p>
        </div>

        {!isLoading && (
          <span
            className="badge"
            style={{
              background: s.bg,

              color: s.fg,

              border: `1px solid ${s.br}`,

              fontWeight: 600,
            }}
          >
            {s.label}
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="skeleton" style={{ height: 160 }} />
      ) : !customer ? (
        <div className="muted">
          We don’t see a linked customer profile yet. If you believe this is a mistake, please
          contact support.
        </div>
      ) : (
        <>
          <div className="grid grid--2" style={{ marginTop: 8 }}>
            {items.map((it) => {
              const uploadedAt = it.doc ? new Date(it.doc.uploadedAt).toLocaleString() : null;

              const niceName =
                it.doc?.id + (it.key === 'AGREEMENT' ? 'agreement' : 'resale-certificate') + '.pdf';

              return (
                <div className="card card--subtle" key={it.key}>
                  <div className="flex-between">
                    <div>
                      <h4 style={{ margin: 0 }}>{it.title}</h4>

                      <div className="muted" style={{ marginTop: 4 }}>
                        {it.required ? 'Required' : 'Optional'}
                      </div>
                    </div>

                    <div style={{ textAlign: 'right' }}>
                      {it.doc && <div style={{ marginTop: 4 }}>{statusPill(it.doc.status)}</div>}

                      <div>
                        {it.doc ? (
                          <span
                            className="badge"
                            style={{
                              background: '#ecfeff',

                              border: '1px solid #a5f3fc',

                              color: '#155e75',
                            }}
                          >
                            v{it.doc.version} · Uploaded {uploadedAt}
                          </span>
                        ) : (
                          <span
                            className="badge"
                            style={{
                              background: '#fee2e2',

                              border: '1px solid #fecaca',

                              color: '#991b1b',
                            }}
                          >
                            Not uploaded
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="doc-actions customer-verification-doc-actions">
                    <button
                      type="button"
                      className="btn btn--ghost doc-btn customer-doc-action customer-doc-action--upload"
                      onClick={() => fileRefs.current[it.key]?.click()}
                      disabled={upload.isPending}
                    >
                      <Upload size={14} />
                      {upload.isPending ? 'Uploading…' : it.doc ? 'Replace PDF' : 'Upload PDF'}
                    </button>

                    {/* Hidden file input */}
                    <input
                      ref={(el) => {
                        fileRefs.current[it.key] = el;
                      }}
                      type="file"
                      accept="application/pdf"
                      style={{ display: 'none' }}
                      onChange={onPick(it.key)}
                    />

                    {it.doc?.fileUrl && (
                      <>
                        <button
                          type="button"
                          className="btn btn--ghost doc-btn customer-doc-action customer-doc-action--preview"
                          onClick={() => handlePreview(it.doc!.id, `${it.title} Preview`, niceName)}
                          disabled={previewingId !== null}
                          aria-label={`Preview ${it.title} PDF`}
                        >
                          <Eye size={14} />
                          {previewingId === it.doc.id ? 'Opening…' : 'Preview'}
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost doc-btn customer-doc-action customer-doc-action--download"
                          onClick={() => downloadDoc.mutate({ docId: it.doc!.id, name: niceName })}
                          disabled={downloadDoc.isPending}
                        >
                          <Download size={14} />
                          {downloadDoc.isPending ? 'Downloading…' : 'Download'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="muted" style={{ marginTop: 10 }}>
            {customer.isAgreementRequered || customer.isResaleCertificateRequered
              ? 'Your organization requires the documents marked as Required before approval.'
              : 'No documents were required in your invite. You can still upload them here if you wish.'}
          </div>
        </>
      )}
      {preview && (
        <PdfPreviewDialog
          url={preview.url}
          title={preview.title}
          onClose={() => setPreview(null)}
          footer={
            <div className="actions">
              <button
                type="button"
                className="btn btn--secondary"
                disabled={downloadDoc.isPending}
                onClick={() => downloadDoc.mutate({ docId: preview.docId, name: preview.filename })}
              >
                <Download size={14} />
                {downloadDoc.isPending ? 'Downloading…' : 'Download PDF'}
              </button>
            </div>
          }
        />
      )}
    </div>
  );
}
